/**
 * RRULE helpers using the rrule package.
 * Used for building recurrence rules and generating human-readable descriptions.
 */

import { RRule, Weekday } from "rrule";

export type RRuleFrequency = "DAILY" | "WEEKLY";

export type DayOfWeek = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export const DAY_LABELS: Record<DayOfWeek, string> = {
  MO: "Mon",
  TU: "Tue",
  WE: "Wed",
  TH: "Thu",
  FR: "Fri",
  SA: "Sat",
  SU: "Sun",
};

export const ALL_DAYS: DayOfWeek[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
export const WEEKDAYS: DayOfWeek[] = ["MO", "TU", "WE", "TH", "FR"];

// Map our day codes to rrule Weekday objects
const DAY_TO_RRULE: Record<DayOfWeek, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

// Map rrule weekday numbers back to our codes
const RRULE_WEEKDAY_TO_DAY: Record<number, DayOfWeek> = {
  0: "MO",
  1: "TU",
  2: "WE",
  3: "TH",
  4: "FR",
  5: "SA",
  6: "SU",
};

export interface RRuleOptions {
  freq: RRuleFrequency;
  interval?: number; // Default 1
  count?: number; // End after N occurrences
  until?: Date; // End on date
  byDay?: DayOfWeek[]; // For weekly: which days
}

/**
 * Build an RRULE string from options.
 * Returns null if no recurrence (single occurrence).
 */
export function buildRRule(options: RRuleOptions | null): string | null {
  if (!options) {
    return null;
  }

  const rule = new RRule({
    freq: options.freq === "DAILY" ? RRule.DAILY : RRule.WEEKLY,
    interval: options.interval ?? 1,
    count: options.count,
    until: options.until,
    byweekday: options.byDay?.map((d) => DAY_TO_RRULE[d]),
  });

  // RRule.toString() returns "RRULE:FREQ=..." - we just want the part after "RRULE:"
  const str = rule.toString();
  return str.replace(/^RRULE:/, "");
}

/**
 * Parse an RRULE string into options.
 */
export function parseRRule(rruleStr: string | null): RRuleOptions | null {
  if (!rruleStr) {
    return null;
  }

  try {
    // RRule.fromString expects "RRULE:" prefix
    const prefixed = rruleStr.startsWith("RRULE:")
      ? rruleStr
      : `RRULE:${rruleStr}`;
    const rule = RRule.fromString(prefixed);

    const options: RRuleOptions = {
      freq: rule.options.freq === RRule.DAILY ? "DAILY" : "WEEKLY",
    };

    if (rule.options.interval && rule.options.interval > 1) {
      options.interval = rule.options.interval;
    }

    if (rule.options.count) {
      options.count = rule.options.count;
    }

    if (rule.options.until) {
      options.until = rule.options.until;
    }

    if (rule.options.byweekday && rule.options.byweekday.length > 0) {
      options.byDay = rule.options.byweekday.map((wd) => {
        // byweekday can be number or Weekday object
        const weekdayNum =
          typeof wd === "number" ? wd : (wd as Weekday).weekday;
        return RRULE_WEEKDAY_TO_DAY[weekdayNum];
      });
    }

    return options;
  } catch {
    return null;
  }
}

/**
 * Generate a human-readable description of an RRULE.
 * Uses rrule's built-in toText() method.
 */
export function describeRRule(rruleStr: string | null): string {
  if (!rruleStr) {
    return "";
  }

  try {
    const prefixed = rruleStr.startsWith("RRULE:")
      ? rruleStr
      : `RRULE:${rruleStr}`;
    const rule = RRule.fromString(prefixed);
    return rule.toText();
  } catch {
    return "";
  }
}

/**
 * Get the day of week code for a given date.
 */
export function getDayOfWeek(date: Date): DayOfWeek {
  const days: DayOfWeek[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  return days[date.getDay()];
}

/**
 * Expand an RRULE to get all occurrences within a date range.
 * Useful for calendar display.
 */
export function expandRRule(
  rruleStr: string,
  dtstart: Date,
  windowStart: Date,
  windowEnd: Date,
): Date[] {
  try {
    const prefixed = rruleStr.startsWith("RRULE:")
      ? rruleStr
      : `RRULE:${rruleStr}`;
    const rule = RRule.fromString(prefixed);

    // Create a new rule with dtstart
    const ruleWithStart = new RRule({
      ...rule.options,
      dtstart,
    });

    return ruleWithStart.between(windowStart, windowEnd, true);
  } catch {
    return [];
  }
}
